/**
 * ReviewListContainer
 * --
 */
import React, { useState, useEffect } from 'react';
import MessageAlert from 'utils';
import { ReviewApi } from 'api';
import ReviewListPresenter from './ReviewListPresenter';
import { useUpsertPointHistory } from 'hooks/PointHistoryHooks';
import { useGetSetting } from 'hooks/SettingHooks';
import dayjs from 'dayjs';
import { useBulkDeleteReview } from 'hooks/ReviewHooks';

/**----------------------------------------
 * [Component] 총관리자: 리뷰목록 - 컨테이너
 * ----------------------------------------
 */
const ReviewListContainer = ({ defaultShopId, permission }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [reviewList, setReviewList] = useState([]);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);
  const [reviewDetailModal, setReviewDetailModal] = useState(null);

  const [selectedList, setSelectedList] = useState([]);

  /* ===== MUTATE ===== */
  const { data: point_setting } = useGetSetting(1, null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return null;

      return data.point_setting;
    },
    initialData: null,
  });

  /* ===== MUTATE ===== */
  const { upsertPointHistory } = useUpsertPointHistory();
  const { bulkDeleteReview } = useBulkDeleteReview();

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**--------------------
   * 리뷰목록 조회
   * --------------------
   */
  const handleGetReviewList = async () => {
    setLoading(true);
    try {
      const { status, message, data } = await ReviewApi.getReviews({
        join_tables: `["user", "product", "review_file", "point_history"]`,
        shop_information_id: currentShop,
      });
      if (status !== 200) throw { message };

      const newData = data?.map((review) => ({
        key: review?.review_id,
        ...review,
        point_history: review?.point_history?.length
          ? review?.point_history?.[0]
          : null,
      }));

      setTimeout(() => {
        setReviewList(
          newData.filter(
            (item) => item?.product?.shop_information_id === currentShop
          )
        );
        setLoading(false);
      }, 750);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 리뷰 수정(노출여부)
   * --------------------
   */
  const handleUpdateReview = async (review_id, value) => {
    setLoading(true);
    try {
      const newData = {
        review_id,
        is_show: value,
      };
      const { status, message } = await ReviewApi.updateReview(newData);
      if (status !== 200) throw { message };

      setTimeout(() => {
        onChangeReviewDetail(newData);
        MessageAlert.info('상태가 변경되었습니다');
        setLoading(false);
      }, 750);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**
   * 상세보기에서 데이터 변경시 반영하는 함수
   * --
   */
  const onChangeReviewDetail = (data) => {
    const fid = reviewList.findIndex(
      (item) => item.review_id === data.review_id
    );

    setReviewList((prev) => [
      ...prev.slice(0, fid),
      {
        ...prev[fid],
        ...data,
      },
      ...prev.slice(fid + 1),
    ]);
  };

  /**
   * 리뷰삭제
   * --
   */
  const handleDeleteReview = (review_id) => {
    setReviewList(reviewList.filter((item) => item.review_id !== review_id));
    setReviewDetailModal(null);
  };

  /**
   * 리뷰 Bulk 삭제
   * --
   */
  const handleBulkDelete = async () => {
    const confirm = window.confirm(
      `${selectedList?.length}개의 리뷰를 삭제하시겠습니까?\n삭제 후 복구할 수 없으며 지급된 포인트는 회수됩니다.`
    );

    if (!confirm) return;

    try {
      const arr = selectedList?.map((item) => ({
        review_id: item?.review_id,
      }));
      const { status } = await bulkDeleteReview(arr);

      if (status !== 200) {
        MessageAlert.info('리뷰 삭제에 실패하였습니다.');
        return;
      }

      MessageAlert.success(
        `${selectedList?.length}개의 리뷰가 삭제되었습니다.`
      );
      setReviewList((prev) =>
        prev?.filter(
          (item) => !arr.some((sub) => sub?.review_id === item?.review_id)
        )
      );
    } catch (err) {
      console.error('[handleBulkDelete] Error!! ', err);
      MessageAlert.error('리뷰 삭제에 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  /**--------------------
   * 리뷰 적립금 지급
   * --------------------
   * @param {object | array} data
   * @param {number} reserve
   */
  const handleReserveReview = async (data, { reserve, memo }, callback) => {
    const confirm = (msg = '') => window.confirm(msg);

    const dataType = Array.isArray(data)
      ? 'array'
      : typeof data === 'object'
      ? 'object'
      : null;

    if (!dataType) return;

    try {
      let arr = [];
      if (dataType === 'array') {
        arr = data.filter((item) => !item.point_history);

        if (!arr.length) {
          MessageAlert.info('적립금 지급 가능한 회원이 없습니다.');
          return;
        }
      }

      const msg =
        dataType === 'array'
          ? data?.length === arr?.length
            ? `${data?.length}명에게 ${reserve} 적립금을 지급하시겠습니까?`
            : `이미 적립된 ${data?.length - arr?.length}명을 제외한 ${
                arr?.length
              }명에게 ${reserve} 적립금을 지급하시겠습니까?`
          : `${data?.user?.user_name}님에게 ${reserve} 적립금을 지급하시겠습니까?`;

      if (!confirm(msg)) return;

      setLoading(true);

      let res, update;
      switch (dataType) {
        case 'array':
          update = data?.map((review) => ({
            review_id: review?.review_id,
            user_id: review?.user_id,
            point: reserve,
            shop_information_id: review?.product?.shop_information_id,
            type: 'EARNED',
            memo: memo,
            expired_date: dayjs()
              .add(point_setting?.expired_date, 'day')
              .unix(),
          }));

          res = await upsertPointHistory(update);

          if (res.status !== 200) {
            MessageAlert.error('적립금 지급에 실패하였습니다!');
            return;
          }

          data.forEach((review) => {
            onChangeReviewDetail({
              ...review,
              point_history: res?.data?.point_history?.find(
                (history) => history.review_id === review.review_id
              ),
            });
          });

          MessageAlert.success('적립금이 지급되었습니다.');
          setSelectedList([]);
          callback && callback();
          break;
        case 'object':
          update = {
            review_id: data?.review_id,
            user_id: data?.user_id,
            point: reserve,
            shop_information_id: data?.product?.shop_information_id,
            type: 'EARNED',
            memo: memo,
            expired_date: dayjs()
              .add(point_setting?.expired_date, 'day')
              .unix(),
          };

          res = await upsertPointHistory([update]);

          if (res.status !== 200) {
            MessageAlert.error('적립금 지급에 실패하였습니다!');
            return;
          }

          onChangeReviewDetail({
            ...data,
            point_history: res?.data?.point_history?.[0],
          });
          setReviewDetailModal((prev) => ({
            ...prev,
            point_history: res?.data?.point_history?.[0],
          }));
          MessageAlert.success('적립금이 지급되었습니다.');

          break;
        default:
          return;
      }
    } catch (err) {
      console.error('[handleReserveReview] Error!! ', err);
      MessageAlert.error('적립금 지급에 실패하였습니다!');
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedList((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedList((prev) =>
          prev.filter((select) => select.key !== obj.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedList(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedList.filter((user) =>
          obj.some((ob) => ob.key === user.key)
        );

        setSelectedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedList.length === obj.length;

        if (!isAll) {
          setSelectedList(obj);
        } else {
          setSelectedList([]);
        }
        return;
      default:
        return;
    }
  };

  /* ===== HOOKS ===== */
  /* 초기 설정 & 업체변경 시 반영 */
  useEffect(() => {
    currentShop && handleGetReviewList();
  }, [currentShop]);

  /* ===== RENDER ===== */
  return (
    <ReviewListPresenter
      {...{
        /* States */
        permission,
        loading,
        reviewList,
        // permission,
        currentShop,

        /* Events */
        setCurrentShop,
        // 리뷰 상세보기 모달
        reviewDetailModal,
        setReviewDetailModal,
        onChangeReviewDetail,
        onUpdateReview: handleUpdateReview,
        onDeleteReview: handleDeleteReview,
        setLoading,
        onReserveReview: handleReserveReview,
        onSelect: handleSelect,
        selectedList,
        onBulkDelete: handleBulkDelete,
      }}
    />
  );
};

/* DF */
ReviewListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default ReviewListContainer;
